import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Autocomplete,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DirectionsBoatOutlined from "@mui/icons-material/DirectionsBoatOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import TaskAltOutlined from "@mui/icons-material/TaskAltOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import CrudService from "services/cruds-service";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../examples/Footer";
import JourneyBar from "./oceanStatus/JourneyBar";
import TrackingDrawer, { CarrierBadge, Pill } from "./oceanStatus/TrackingDrawer";
import {
    CARRIER_SHORT,
    STANDARD_LABEL,
    TONE,
    arrivalHint,
    arrivalOf,
    arrivingWithinDays,
    errorText,
    formatDate,
    istanbulDayEnd,
    istanbulDayStart,
    istanbulStamp,
    TZ_LABEL,
    portText,
    rowState,
    timeAgo,
} from "./oceanStatus/constants";

/*
 * Ocean Shipment Status: container visibility from the carriers' own APIs
 * (DCSA Track & Trace), normalized to Filentech standard milestones.
 */

const AUTO_REFRESH_MS = 5 * 60 * 1000;

// KPI cards double as the main filter.
const VIEWS = [
    { key: "transit", label: "In Transit", tone: "blue", match: (t) => ["transit", "pending"].includes(rowState(t).key) },
    { key: "arriving", label: "Arriving in 7 days", tone: "blue", match: (t) => arrivingWithinDays(t, 7) },
    { key: "delayed", label: "Delayed", tone: "amber", match: (t) => rowState(t).key === "delayed" },
    { key: "exception", label: "Exceptions", tone: "red", match: (t) => rowState(t).key === "exception" },
    { key: "attention", label: "Needs Attention", tone: "slate", match: (t) => rowState(t).key === "attention" },
    { key: "completed", label: "Completed", tone: "green", match: (t) => rowState(t).key === "completed" },
];

const KPI_HINT = {
    transit: (rows) => `${rows.filter((t) => t.currentStatus === "VESSEL_DEPARTED" || t.currentStatus?.startsWith("TRANSSHIPMENT")).length} on the water`,
    arriving: (rows) => `${rows.filter((t) => arrivingWithinDays(t, 2)).length} within 48h`,
    delayed: (rows) => {
        const hours = rows.map((t) => t.delayHours || 0).filter(Boolean);
        return hours.length ? `avg +${(hours.reduce((a, b) => a + b, 0) / hours.length / 24).toFixed(1)} days` : "vs. first ETA";
    },
    exception: () => "missed ETD / ETA",
    attention: () => "API error · not found · not connected",
    completed: () => "empty returned",
};

const EMPTY_FILTERS = { search: "", carrier: "", pol: "", pod: "", customer: "", etaFrom: "", etaTo: "" };

const csvCell = (value) => {
    const text = String(value ?? "");
    return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const VIEW_ICONS = {
    transit: DirectionsBoatOutlined,
    arriving: EventAvailableOutlined,
    delayed: ScheduleOutlined,
    exception: ReportProblemOutlined,
    attention: ErrorOutlineIcon,
    completed: TaskAltOutlined,
};

// Sade kurumsal KPI kartı: ikon + etiket, tek renkli değer, tek satır açıklama, toplam içindeki pay çubuğu
function KpiCard({ view, count, hint, total, active, onClick }) {
    const tone = TONE[view.tone];
    const Icon = VIEW_ICONS[view.key];
    const share = total ? Math.round((count / total) * 100) : 0;
    return (
        <MDBox
            onClick={onClick}
            role="button"
            tabIndex={0}
            aria-pressed={active}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            sx={{
                position: "relative",
                cursor: "pointer",
                height: "100%",
                p: 2,
                pl: 2.25,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                overflow: "hidden",
                borderRadius: "10px",
                background: active ? tone.bg : "#fff",
                border: `1px solid ${active ? tone.color : "rgba(15, 23, 42, 0.12)"}`,
                transition: "border-color .15s ease, background .15s ease",
                "&:hover": { borderColor: tone.color },
                "&:focus-visible": { outline: `2px solid ${tone.color}`, outlineOffset: 2 },
                // Seçili kartta sol kenar şeridi
                "&::before": {
                    content: "\"\"",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: active ? tone.color : "transparent",
                },
            }}
        >
            <MDBox display="flex" alignItems="center" gap={1}>
                <MDBox
                    width={28}
                    height={28}
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ borderRadius: "7px", background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color }}
                >
                    {Icon && <Icon sx={{ fontSize: "17px !important", color: "inherit" }} />}
                </MDBox>
                <MDTypography sx={{ fontSize: 12, fontWeight: 700, color: "#475467", letterSpacing: "0.3px", textTransform: "uppercase", lineHeight: 1.2 }} noWrap>
                    {view.label}
                </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="baseline" gap={1}>
                <MDTypography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: count ? "#101828" : "#98A2B3" }}>
                    {count}
                </MDTypography>
                {total > 0 && (
                    <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#98A2B3" }}>{share}%</MDTypography>
                )}
            </MDBox>

            <Tooltip title={hint} arrow placement="bottom-start">
                <MDTypography sx={{ fontSize: 12, color: "#667085", lineHeight: 1.35 }} noWrap>
                    {hint}
                </MDTypography>
            </Tooltip>

            <MDBox sx={{ height: 4, borderRadius: 99, background: "#EEF2F6", overflow: "hidden", mt: "auto" }}>
                <MDBox sx={{ width: `${share}%`, height: "100%", background: count ? tone.color : "transparent", transition: "width .3s ease" }} />
            </MDBox>
        </MDBox>
    );
}

function EmptyState({ filtered, onClear, onAdd }) {
    return (
        <MDBox py={7} textAlign="center">
            <svg width="96" height="72" viewBox="0 0 96 72" fill="none" aria-hidden="true">
                <path d="M8 52h80l-8 12H18L8 52z" fill="#e0ecff" />
                <rect x="24" y="30" width="16" height="22" rx="2" fill="#93c5fd" />
                <rect x="42" y="22" width="16" height="30" rx="2" fill="#60a5fa" />
                <rect x="60" y="34" width="14" height="18" rx="2" fill="#bfdbfe" />
                <path d="M4 68c8 0 8-4 16-4s8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4" stroke="#bfdbfe" strokeWidth="2" />
            </svg>
            <MDTypography variant="h6" mt={1.5}>
                {filtered ? "No trackings match these filters" : "No containers tracked yet"}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={2}>
                {filtered
                    ? "Try another view or clear the filters."
                    : "Add a container, B/L or booking number to start receiving events from the carrier."}
            </MDTypography>
            {filtered ? (
                <MDButton variant="outlined" color="info" size="small" onClick={onClear}>
                    Clear filters
                </MDButton>
            ) : (
                <MDButton variant="gradient" color="dark" size="small" startIcon={<AddIcon />} onClick={onAdd}>
                    Add Tracking
                </MDButton>
            )}
        </MDBox>
    );
}

function OceanShipmentStatus() {
    const location = useLocation();
    const [trackings, setTrackings] = useState([]);
    const [carriers, setCarriers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastLoadedAt, setLastLoadedAt] = useState(null);
    const [, setClock] = useState(0); // re-renders "updated x min ago"
    const [notification, setNotification] = useState({ value: false, text: "", color: "info" });
    const [refreshingId, setRefreshingId] = useState(null);

    const [view, setView] = useState("");
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sort, setSort] = useState({ by: "eta", dir: "asc" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const [addOpen, setAddOpen] = useState(false);
    const [shipments, setShipments] = useState([]);
    const [form, setForm] = useState({ shipment: null, carrier: "", containers: "", bookingNumber: "", mblNumber: "" });
    const [saving, setSaving] = useState(false);

    // An older timer must not hide a newer message.
    const notifyTimer = useRef(null);
    const notify = (text, color = "info") => {
        setNotification({ value: true, text, color });
        clearTimeout(notifyTimer.current);
        notifyTimer.current = setTimeout(() => setNotification({ value: false, text: "", color: "info" }), 6000);
    };

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const [trackingResponse, carrierResponse] = await Promise.all([
                CrudService.getOceanTrackings(),
                CrudService.getOceanTrackingCarriers(),
            ]);
            setTrackings((trackingResponse?.data || []).map((item) => ({ id: item.id, ...item.attributes })));
            setCarriers(carrierResponse?.data?.carriers || []);
            setLastLoadedAt(new Date());
        } catch (error) {
            if (!silent) notify(errorText(error, "Tracking records could not be loaded."), "error");
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const refresh = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS);
        const tick = setInterval(() => setClock((c) => c + 1), 30000);
        return () => {
            clearInterval(refresh);
            clearInterval(tick);
            clearTimeout(notifyTimer.current);
        };
    }, [load]);

    // Deep links from Ocean Shipments: ?search=CONTAINER and/or ?open=<trackingId>
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const search = params.get("search");
        if (search) setFilters((f) => ({ ...f, search }));
        const open = params.get("open");
        if (open) openDetail(open);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setPage(0);
    };

    const baseRows = useMemo(() => {
        const q = filters.search.trim().toLowerCase();
        // ETA range is a Türkiye calendar day range.
        const from = istanbulDayStart(filters.etaFrom);
        const to = istanbulDayEnd(filters.etaTo);

        return trackings.filter((t) => {
            // Sample data from a carrier's mock endpoint is never listed.
            if (t.isDemo) return false;
            if (filters.carrier && t.carrier !== filters.carrier) return false;
            if (filters.customer && t.customer !== filters.customer) return false;
            if (filters.pol && !`${portText(t.pol)} ${t.pol?.name || ""}`.toLowerCase().includes(filters.pol.toLowerCase())) return false;
            if (filters.pod && !`${portText(t.pod)} ${t.pod?.name || ""}`.toLowerCase().includes(filters.pod.toLowerCase())) return false;
            if (from || to) {
                const arrival = arrivalOf(t) ? new Date(arrivalOf(t)).getTime() : null;
                if (!arrival || (from && arrival < from) || (to && arrival > to)) return false;
            }
            if (q) {
                const haystack = [t.containerNumber, t.bookingNumber, t.mblNumber, t.shipmentNo, t.customer, t.vesselName, t.voyageNumber]
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            return true;
        });
    }, [trackings, filters]);

    const counts = useMemo(() => Object.fromEntries(VIEWS.map((v) => [v.key, baseRows.filter(v.match)])), [baseRows]);

    const rows = useMemo(() => {
        const matcher = VIEWS.find((v) => v.key === view)?.match;
        const list = matcher ? baseRows.filter(matcher) : baseRows;
        const dir = sort.dir === "asc" ? 1 : -1;
        const value = (t) => {
            if (sort.by === "eta") return arrivalOf(t) ? new Date(arrivalOf(t)).getTime() : Number.MAX_SAFE_INTEGER;
            if (sort.by === "container") return t.containerNumber || "";
            if (sort.by === "carrier") return CARRIER_SHORT[t.carrier] || t.carrier;
            if (sort.by === "status") return ["exception", "attention", "delayed", "transit", "pending", "paused", "completed"].indexOf(rowState(t).key);
            return 0;
        };
        return [...list].sort((a, b) => (value(a) > value(b) ? dir : value(a) < value(b) ? -dir : 0));
    }, [baseRows, view, sort]);

    const customers = useMemo(() => [...new Set(trackings.map((t) => t.customer).filter(Boolean))].sort(), [trackings]);
    const filtersActive = Boolean(view) || Object.values(filters).some(Boolean);

    const mergeRow = (id, attributes) =>
        setTrackings((prev) => prev.map((t) => (t.id === id ? { ...t, ...attributes, id } : t)));

    const openDetail = async (id) => {
        setDetail((prev) => (prev?.id === id ? prev : { id }));
        setDetailLoading(true);
        try {
            const response = await CrudService.getOceanTracking(id);
            setDetail({ id, ...response.data.attributes, events: response.data.events || [] });
        } catch (error) {
            notify(errorText(error, "Tracking detail could not be loaded."), "error");
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleRefresh = async (id) => {
        setRefreshingId(id);
        try {
            const response = await CrudService.refreshOceanTracking(id);
            mergeRow(id, response.data.attributes);
            if (response.meta?.status === "OK") notify("Updated from the carrier.", "success");
            else notify(response.meta?.error || "Carrier did not return data.", "warning");
            if (detail?.id === id) openDetail(id);
        } catch (error) {
            notify(errorText(error, "Refresh failed."), "error");
        } finally {
            setRefreshingId(null);
        }
    };

    const handleToggleActive = async (row) => {
        try {
            const response = await CrudService.updateOceanTracking(row.id, { active: !row.active });
            mergeRow(row.id, response.data.attributes);
            setDetail((prev) => (prev?.id === row.id ? { ...prev, ...response.data.attributes } : prev));
        } catch (error) {
            notify(errorText(error, "Update failed."), "error");
        }
    };

    const handleDelete = async () => {
        const row = confirmDelete;
        setConfirmDelete(null);
        try {
            await CrudService.deleteOceanTracking(row.id);
            setTrackings((prev) => prev.filter((t) => t.id !== row.id));
            if (detail?.id === row.id) setDetail(null);
            notify("Tracking deleted.", "success");
        } catch (error) {
            notify(errorText(error, "Delete failed."), "error");
        }
    };

    const exportCsv = () => {
        const header = ["Container", "Type", "Carrier", "SCAC", "Booking", "MBL", "Shipment No", "Customer", "Vessel", "Voyage", "IMO", "POL", "T/S", "POD", "ETD (TRT)", "ATD (TRT)", "ETA (TRT)", "ATA (TRT)", "Milestone", "Status", "Delay (h)", "Last carrier update (TRT)"];
        const iso = istanbulStamp; // Türkiye time
        const lines = rows.map((t) =>
            [
                t.containerNumber, t.containerType, CARRIER_SHORT[t.carrier] || t.carrier, t.scac, t.bookingNumber, t.mblNumber,
                t.shipmentNo, t.customer, t.vesselName, t.voyageNumber, t.imoNumber, portText(t.pol),
                (t.transshipmentPorts || []).map(portText).join(" / "), portText(t.pod), iso(t.etd), iso(t.atd), iso(t.eta), iso(t.ata),
                STANDARD_LABEL[t.currentStatus] || "", rowState(t).label, Math.round(t.delayHours || 0), iso(t.lastApiUpdate),
            ]
                .map(csvCell)
                .join(";")
        );
        // BOM + ";" so Excel (TR locale) opens it with the right columns and characters.
        const blob = new Blob([`﻿${[header.join(";"), ...lines].join("\r\n")}`], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `ocean-shipment-status-${istanbulStamp(new Date()).slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // ---- Add tracking ----
    const openAdd = async () => {
        setForm({ shipment: null, carrier: "", containers: "", bookingNumber: "", mblNumber: "" });
        setAddOpen(true);
        if (!shipments.length) {
            try {
                const response = await CrudService.getOceanShipments();
                setShipments((response?.data || []).map((item) => ({ id: item.id, ...item.attributes })));
            } catch (error) {
                notify(errorText(error, "Ocean shipments could not be loaded."), "error");
            }
        }
    };

    const carrierFromLine = (line) => {
        const name = String(line || "").toUpperCase();
        if (!name) return "";
        if (name.includes("HAPAG")) return "HAPAG_LLOYD";
        if (name.includes("CMA")) return "CMA_CGM";
        if (name.includes("MAERSK")) return "MAERSK";
        if (name === "MSC") return "MSC";
        if (name.startsWith("ONE")) return "ONE";
        return "";
    };

    const selectShipment = (shipment) => {
        const containers = new Set();
        (shipment?.cargoDetails || []).forEach((cargo) => {
            if (cargo.containerNo) containers.add(cargo.containerNo);
            (cargo.containers || []).forEach((c) => c.containerNo && containers.add(c.containerNo));
        });
        setForm((prev) => ({
            ...prev,
            shipment,
            carrier: carrierFromLine(shipment?.shippingLine) || prev.carrier,
            containers: [...containers].join("\n"),
        }));
    };

    const handleCreate = async () => {
        const containerNumbers = form.containers
            .split(/[\s,;]+/)
            .map((c) => c.trim())
            .filter(Boolean);
        if (!containerNumbers.length && !form.bookingNumber.trim() && !form.mblNumber.trim()) {
            notify("Enter at least one container, B/L or booking number.", "warning");
            return;
        }

        setSaving(true);
        try {
            const response = await CrudService.createOceanTrackings({
                oceanShipmentId: form.shipment?.id || undefined,
                carrier: form.carrier || undefined,
                containerNumbers,
                bookingNumber: form.bookingNumber.trim(),
                mblNumber: form.mblNumber.trim(),
            });
            setAddOpen(false);
            await load({ silent: true });

            const created = response?.data || [];
            const { skipped = [], warnings = [] } = response?.meta || {};
            const parts = [`${created.length} tracking(s) created.`];
            if (skipped.length) parts.push(`${skipped.length} already tracked.`);
            if (warnings.length) parts.push(`Check: ${warnings.join("; ")}`);
            notify(parts.join(" "), warnings.length ? "warning" : "success");
        } catch (error) {
            notify(errorText(error, "Tracking could not be created."), "error");
        } finally {
            setSaving(false);
        }
    };

    const headCell = (id, label, sortable = true) => (
        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, py: 1.25, borderBottom: "1px solid #e5e7eb" }}>
            {sortable ? (
                <TableSortLabel
                    active={sort.by === id}
                    direction={sort.by === id ? sort.dir : "asc"}
                    onClick={() => setSort((s) => ({ by: id, dir: s.by === id && s.dir === "asc" ? "desc" : "asc" }))}
                >
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    );

    const cardSx = { borderRadius: "16px", border: "1px solid #e5e7eb", background: "#fff", boxShadow: "0 8px 26px rgba(15,23,42,0.05)" };
    const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDBox sx={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 2000, width: "min(640px, calc(100% - 32px))" }}>
                    <MDAlert color={notification.color}>
                        <MDTypography variant="body2" color="white">
                            {notification.text}
                        </MDTypography>
                    </MDAlert>
                </MDBox>
            )}

            <MDBox pt={6} pb={3}>
                {/* Header */}
                <MDBox p={2.5} mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ ...cardSx, background: "linear-gradient(135deg,#f8fbff 0%,#fff 100%)" }}>
                    <MDBox display="flex" alignItems="center" gap={2}>
                        <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ width: 48, height: 48, borderRadius: 3, background: "linear-gradient(135deg,#49a3f1 0%,#1a73e8 100%)", color: "#fff", flexShrink: 0 }}>
                            <SatelliteAltIcon fontSize="medium" />
                        </MDBox>
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="bold">
                                Ocean Shipment Status
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                                Live container milestones from carrier APIs · updated {timeAgo(lastLoadedAt)} · auto-refresh every 5 min · all times in {TZ_LABEL}
                            </MDTypography>
                            <MDBox display="flex" gap={1.5} mt={0.75} flexWrap="wrap">
                                {carriers.map((c) => (
                                    <Tooltip key={c.code} arrow title={c.configured ? `${c.name}: API credentials configured` : `${c.name}: API not connected yet`}>
                                        <MDBox component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 11.5, color: c.configured ? "#334155" : "#94a3b8" }}>
                                            <MDBox component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: c.configured ? "#16a34a" : "#cbd5e1" }} />
                                            {CARRIER_SHORT[c.code] || c.name}
                                        </MDBox>
                                    </Tooltip>
                                ))}
                            </MDBox>
                        </MDBox>
                    </MDBox>
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                        <MDButton variant="outlined" color="info" size="small" onClick={() => load()} startIcon={<SyncIcon />}>
                            Reload
                        </MDButton>
                        <MDButton variant="outlined" color="dark" size="small" onClick={exportCsv} startIcon={<FileDownloadOutlinedIcon />} disabled={!rows.length}>
                            Export (Excel)
                        </MDButton>
                        <MDButton variant="gradient" color="dark" size="small" onClick={openAdd} startIcon={<AddIcon />}>
                            Add Tracking
                        </MDButton>
                    </MDBox>
                </MDBox>

                {/* KPI cards */}
                <Grid container spacing={2} mb={2}>
                    {VIEWS.map((v) => (
                        <Grid item xs={6} md={4} lg={2} key={v.key}>
                            {loading ? (
                                <Skeleton variant="rounded" height={128} sx={{ borderRadius: "10px" }} />
                            ) : (
                                <KpiCard
                                    view={v}
                                    count={counts[v.key].length}
                                    hint={KPI_HINT[v.key](counts[v.key])}
                                    total={baseRows.length}
                                    active={view === v.key}
                                    onClick={() => {
                                        setView((current) => (current === v.key ? "" : v.key));
                                        setPage(0);
                                    }}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>

                {/* Filters + table */}
                <MDBox sx={{ ...cardSx, overflow: "hidden" }}>
                    <MDBox p={2} display="flex" gap={1.5} flexWrap="wrap" alignItems="center" sx={{ borderBottom: "1px solid #eef2f7" }}>
                        <TextField
                            size="small"
                            placeholder="Container, B/L, booking, shipment, customer, vessel"
                            value={filters.search}
                            onChange={(e) => setFilter("search", e.target.value)}
                            sx={{ minWidth: 300, flex: "1 1 300px" }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                        <TextField select size="small" label="Carrier" value={filters.carrier} onChange={(e) => setFilter("carrier", e.target.value)} sx={{ minWidth: 140 }} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                            <MenuItem value="">All carriers</MenuItem>
                            {carriers.map((c) => (
                                <MenuItem key={c.code} value={c.code}>
                                    {CARRIER_SHORT[c.code] || c.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Autocomplete
                            size="small"
                            options={customers}
                            value={filters.customer || null}
                            onChange={(e, v) => setFilter("customer", v || "")}
                            sx={{ minWidth: 200 }}
                            renderInput={(params) => <TextField {...params} label="Customer" InputLabelProps={{ shrink: true }} placeholder="All customers" />}
                        />
                        <TextField size="small" label="POL" value={filters.pol} onChange={(e) => setFilter("pol", e.target.value)} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} placeholder="e.g. TRMER" />
                        <TextField size="small" label="POD" value={filters.pod} onChange={(e) => setFilter("pod", e.target.value)} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} placeholder="e.g. DEHAM" />
                        <TextField size="small" type="date" label="ETA from" value={filters.etaFrom} onChange={(e) => setFilter("etaFrom", e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                        <TextField size="small" type="date" label="ETA to" value={filters.etaTo} onChange={(e) => setFilter("etaTo", e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                        {filtersActive && (
                            <MDButton variant="text" color="info" size="small" onClick={() => { setFilters(EMPTY_FILTERS); setView(""); setPage(0); }}>
                                Clear filters
                            </MDButton>
                        )}
                    </MDBox>

                    {view && (
                        <MDBox px={2} pt={1.5}>
                            <Chip size="small" label={`View: ${VIEWS.find((v) => v.key === view)?.label}`} onDelete={() => setView("")} />
                        </MDBox>
                    )}

                    <MDBox sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 980 }}>
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    {headCell("container", "Container")}
                                    {headCell("carrier", "Carrier")}
                                    {headCell("journey", "Journey", false)}
                                    {headCell("eta", "Arrival")}
                                    {headCell("status", "Status")}
                                    <TableCell sx={{ borderBottom: "1px solid #e5e7eb", width: 120 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading &&
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <TableRow key={`s-${i}`}>
                                            {[160, 110, 280, 120, 120, 24].map((w, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton variant="text" width={w} />
                                                    {j < 4 && <Skeleton variant="text" width={w * 0.6} />}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}

                                {!loading &&
                                    pageRows.map((t) => {
                                        const state = rowState(t);
                                        const hint = arrivalHint(t);
                                        return (
                                            <TableRow
                                                key={t.id}
                                                hover
                                                onClick={() => openDetail(t.id)}
                                                sx={{
                                                    cursor: "pointer",
                                                    "& td": { borderBottom: "1px solid #f1f5f9", py: 1.5 },
                                                    "& td:first-of-type": { borderLeft: `3px solid ${state.tone === "slate" ? "transparent" : TONE[state.tone].color}` },
                                                }}
                                            >
                                                <TableCell>
                                                    <MDBox display="flex" alignItems="center" gap={1}>
                                                        <MDTypography variant="button" fontWeight="bold" sx={{ letterSpacing: 0.3 }}>
                                                            {t.containerNumber || t.mblNumber || t.bookingNumber}
                                                        </MDTypography>
                                                        {t.containerType && (
                                                            <MDTypography variant="caption" color="text">
                                                                {t.containerType}
                                                            </MDTypography>
                                                        )}
                                                        {t.isDemo && <Pill>Demo</Pill>}
                                                    </MDBox>
                                                    <MDTypography variant="caption" color="text" display="block" sx={{ maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {[t.shipmentNo, t.customer].filter(Boolean).join(" · ") || (t.bookingNumber ? `BKG ${t.bookingNumber}` : t.mblNumber ? `MBL ${t.mblNumber}` : "Not linked to a shipment")}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <CarrierBadge code={t.carrier} name={t.carrierName} />
                                                    <MDTypography variant="caption" color="text" display="block">
                                                        {[t.vesselName, t.voyageNumber].filter(Boolean).join(" / ") || "Vessel not reported"}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <JourneyBar tracking={t} compact />
                                                    <MDTypography variant="caption" sx={{ color: "#475569" }} display="block" mt={0.25}>
                                                        {t.currentStatus ? STANDARD_LABEL[t.currentStatus] : "Waiting for first event"}
                                                        {t.lastEventLocation?.locode && t.currentStatus ? ` · ${t.lastEventLocation.name || t.lastEventLocation.locode}` : ""}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell>
                                                    <MDTypography variant="button" fontWeight="medium" display="block">
                                                        {t.ata ? "ATA " : t.eta ? "ETA " : ""}
                                                        {formatDate(arrivalOf(t))}
                                                    </MDTypography>
                                                    {hint && <Pill tone={hint.tone}>{hint.text}</Pill>}
                                                </TableCell>
                                                <TableCell>
                                                    <MDBox display="flex" alignItems="center" gap={0.75}>
                                                        <Pill tone={state.tone}>{state.label}</Pill>
                                                        {(t.lastError || t.exceptionReason) && (
                                                            <Tooltip arrow title={t.exceptionReason || t.lastError}>
                                                                <ErrorOutlineIcon sx={{ fontSize: "18px !important", color: TONE[state.tone].color }} />
                                                            </Tooltip>
                                                        )}
                                                    </MDBox>
                                                    {t.delayHours >= 24 && state.key !== "completed" && (
                                                        <MDTypography variant="caption" sx={{ color: TONE.amber.color }}>
                                                            +{(t.delayHours / 24).toFixed(1)} days vs first ETA
                                                        </MDTypography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {refreshingId === t.id ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <MDButton
                                                            variant="gradient"
                                                            color="info"
                                                            size="small"
                                                            endIcon={<ChevronRightIcon />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDetail(t.id);
                                                            }}
                                                            sx={{ whiteSpace: "nowrap", px: 1.75, boxShadow: "0 4px 12px rgba(26,115,232,0.25)" }}
                                                        >
                                                            Details
                                                        </MDButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </MDBox>

                    {!loading && !rows.length && (
                        <EmptyState
                            filtered={trackings.length > 0}
                            onAdd={openAdd}
                            onClear={() => {
                                setFilters(EMPTY_FILTERS);
                                setView("");
                            }}
                        />
                    )}

                    {!loading && rows.length > 0 && (
                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[15, 30, 50]}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            sx={{ borderTop: "1px solid #eef2f7", "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 12, mb: 0 } }}
                        />
                    )}
                </MDBox>
            </MDBox>

            <TrackingDrawer
                open={Boolean(detail)}
                detail={detail}
                loading={detailLoading}
                refreshing={Boolean(detail && refreshingId === detail.id)}
                onClose={() => setDetail(null)}
                onRefresh={handleRefresh}
                onToggleActive={handleToggleActive}
                onDelete={(row) => setConfirmDelete(row)}
            />

            {/* Delete confirmation */}
            <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete tracking?</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text">
                        <strong>{confirmDelete?.containerNumber || confirmDelete?.mblNumber || confirmDelete?.bookingNumber}</strong> and all carrier
                        events stored for it will be removed. This cannot be undone.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton variant="text" color="secondary" onClick={() => setConfirmDelete(null)}>
                        Cancel
                    </MDButton>
                    <MDButton variant="gradient" color="error" onClick={handleDelete}>
                        Delete
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Add tracking */}
            <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Tracking</DialogTitle>
                <DialogContent>
                    <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                        <Autocomplete
                            options={shipments}
                            value={form.shipment}
                            onChange={(e, value) => selectShipment(value)}
                            getOptionLabel={(s) => [s.shipmentNo, typeof s.customer === "string" ? s.customer : "", s.shippingLine].filter(Boolean).join(" · ")}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => <TextField {...params} label="Ocean Shipment (optional)" placeholder="Search shipment no" />}
                        />
                        <TextField
                            select
                            label="Carrier"
                            value={form.carrier}
                            onChange={(e) => setForm((prev) => ({ ...prev, carrier: e.target.value }))}
                            helperText="Left empty: detected from shipping line, B/L or container prefix."
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{ displayEmpty: true, sx: { height: 44 } }}
                        >
                            <MenuItem value="">Auto-detect</MenuItem>
                            {carriers.map((c) => (
                                <MenuItem key={c.code} value={c.code}>
                                    {c.name} {c.configured ? "" : "(not configured)"}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Container Numbers"
                            multiline
                            minRows={3}
                            value={form.containers}
                            onChange={(e) => setForm((prev) => ({ ...prev, containers: e.target.value }))}
                            helperText="One per line. Each container is tracked separately; a missing check digit is added automatically."
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Booking Number" value={form.bookingNumber} onChange={(e) => setForm((prev) => ({ ...prev, bookingNumber: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="MBL Number" value={form.mblNumber} onChange={(e) => setForm((prev) => ({ ...prev, mblNumber: e.target.value }))} />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton variant="text" color="secondary" onClick={() => setAddOpen(false)} disabled={saving}>
                        Cancel
                    </MDButton>
                    <MDButton variant="gradient" color="dark" onClick={handleCreate} disabled={saving}>
                        {saving ? <CircularProgress size={18} color="inherit" /> : "Start Tracking"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default OceanShipmentStatus;
